import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { DELIVERY_PALETTE } from "@/core/colors";
import {
  isPayrollStatementDeliveryFailed,
  isTextOnlyDelivery,
} from "./delivery-status-rules";
import { SmsDeliveryStatus } from "./sms-delivery-status";
import type { PayrollStatementNotificationDelivery } from "./types";
import {
  formatNotificationTimestamp,
  getDeliveryTimestamp,
  getNotificationReasonLabel,
} from "./utils";

export const DeliveryHistory = ({
  deliveries,
}: {
  deliveries: PayrollStatementNotificationDelivery[];
}) => {
  if (deliveries.length === 0) {
    return (
      <Empty className={emptyStateVariants({ layout: "collection" })}>
        <EmptyHeader>
          <EmptyDescription>
            Nothing sent to this technician yet.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Stack align="start" gap={6} className="w-full px-5 pb-8">
      {deliveries.map((delivery, index) => {
        const isFailed = isPayrollStatementDeliveryFailed(delivery.status);
        const timestamp = formatNotificationTimestamp(
          getDeliveryTimestamp(delivery),
        );
        const key =
          delivery.id ?? delivery.delivery_id ?? `${delivery.status}-${index}`;

        return (
          <Stack
            className="w-full"
            key={key}
            direction="row"
            align="start"
            gap={4}
          >
            <div
              className={cn(
                "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                {
                  [cn(
                    DELIVERY_PALETTE.failed.surface,
                    DELIVERY_PALETTE.failed.foreground,
                  )]: isFailed,
                  [cn(
                    DELIVERY_PALETTE.sent.surface,
                    DELIVERY_PALETTE.sent.foreground,
                  )]: !isFailed,
                },
              )}
            >
              {isFailed ? (
                <XCircleIcon className="h-4 w-4" />
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
            </div>
            <Stack align="start" gap={1} className="min-w-0 flex-1">
              <Text variant="label">
                {isFailed
                  ? "Send failed"
                  : isTextOnlyDelivery(delivery)
                    ? "PayPack texted"
                    : "PayPack emailed"}
              </Text>
              {timestamp && (
                <Text variant="caption" tone="muted">
                  {timestamp}
                </Text>
              )}
              {delivery.actor_name && (
                <Text variant="caption" tone="muted">
                  by {delivery.actor_name}
                </Text>
              )}
              {delivery.note && (
                <Text
                  variant="caption"
                  tone="muted"
                  className="mt-1 border-l-2 border-line pl-2 whitespace-pre-line"
                >
                  {delivery.note}
                </Text>
              )}
              {delivery.error && (
                <Text variant="caption" tone="destructive">
                  {getNotificationReasonLabel(delivery.error) ?? delivery.error}
                </Text>
              )}
              {delivery.sms && <SmsDeliveryStatus sms={delivery.sms} />}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
